/**
 * BillPaymentsPage — `/financial-services/bill-payments`.
 *
 * Slice 9: wired to `GET /v1/financial-services/bill-payments` ledger
 * and `GET /v1/wallet/bill-providers` for the provider catalogue.
 */
import { type JSX, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Droplets, ShieldCheck, Tv, Wifi, Zap } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type BillPaymentStatus,
  type BillProvider,
  listBillPaymentsLedger,
  listBillProviders,
} from '../../lib/api/financial-services';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const CATEGORY_ICONS: Readonly<Record<string, typeof Zap>> = {
  ELECTRICITY: Zap,
  WATER:       Droplets,
  INTERNET:    Wifi,
  TV:          Tv,
  INSURANCE:   ShieldCheck,
};

const STATUS_OPTIONS: readonly { value: BillPaymentStatus | ''; label: string }[] = [
  { value: '',          label: 'All statuses' },
  { value: 'PENDING',   label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED',    label: 'Failed' },
];

export function BillPaymentsPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<BillPaymentStatus | ''>('');
  const [search, setSearch] = useState('');

  const ledger = useQuery({
    queryKey: ['financial-services', 'bill-payments-ledger', statusFilter],
    queryFn: () => listBillPaymentsLedger({ status: statusFilter || undefined, pageSize: 50 }),
    retry: false,
  });

  const providers = useQuery({
    queryKey: ['financial-services', 'bill-providers'],
    queryFn: () => listBillProviders(),
    retry: false,
  });

  const filteredItems = useMemo(() => {
    const items = ledger.data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (p) =>
        p.id.toLowerCase().includes(needle) ||
        p.providerCode.toLowerCase().includes(needle) ||
        (p.providerRef?.toLowerCase().includes(needle) ?? false),
    );
  }, [ledger.data, search]);

  const grouped = useMemo(() => {
    const all = providers.data?.items ?? [];
    const groups = new Map<string, BillProvider[]>();
    for (const p of all) {
      const arr = groups.get(p.category) ?? [];
      arr.push(p);
      groups.set(p.category, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [providers.data]);

  const denied =
    ledger.isError && ledger.error instanceof ApiError &&
    (ledger.error.status === 401 || ledger.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bill payments"
        description="Bill-payment ledger + provider catalogue."
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by id, provider code, or ref…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as BillPaymentStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
          aria-label="Status filter"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </FilterBar>

      {/* Ledger */}
      {ledger.isPending ? (
        <p className="text-sm text-muted">Loading transactions…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Financials/readBillPayments</code> required.
        </DeferredNotice>
      ) : ledger.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {(ledger.error as Error).message}
        </DeferredNotice>
      ) : filteredItems.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No bill-payment transactions found.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Payment ledger</h3>
            <span className="text-xs text-muted">{ledger.data?.total ?? 0} total</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-[10px] uppercase tracking-wider text-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-2">Id</th>
                <th className="text-left px-4 py-2">Provider code</th>
                <th className="text-left px-4 py-2">Provider ref</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredItems.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-[10px] text-gray-900">{p.id}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted">{p.providerCode}</td>
                  <td className="px-4 py-2 text-xs text-muted">{p.providerRef ?? '—'}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Provider catalogue */}
      {grouped.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Provider catalogue</h3>
          {grouped.map(([category, items]) => {
            const Icon = CATEGORY_ICONS[category] ?? ShieldCheck;
            return (
              <div key={category} className="bg-white border border-border rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-border flex items-center gap-2">
                  <Icon className="w-4 h-4 text-nipost-green" />
                  <h4 className="text-sm font-semibold text-gray-900">{category}</h4>
                  <span className="text-[10px] text-muted ml-auto">
                    {items.length} provider{items.length === 1 ? '' : 's'}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white text-[10px] uppercase tracking-wider text-muted border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-2">Provider</th>
                      <th className="text-left px-4 py-2">Code</th>
                      <th className="text-left px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">{p.displayName}</td>
                        <td className="px-4 py-2 font-mono text-xs text-muted">{p.code}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              p.active
                                ? 'bg-success-light text-success'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {p.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
